import { MutedAlertCameras, MutedAlertSites } from "./alertsInterfaces";

// Response of GET monitoring-api/streaming/mutes/ (ENG-2103): the fleet-wide
// mute state, already trimmed to the caller's visible sites with expired
// mutes dropped. expires_at / muted_at are epoch SECONDS.
export interface SharedMuteSiteRow {
  site_id: number;
  site_name: string;
  // Any camera of the site (its lowest pk; monitoring-api #220) for the
  // site-scope unmute; null when the site has no cameras.
  camera_id?: number | null;
  expires_at: number;
  muted_at: number;
  muted_by: string | null;
}

export interface SharedMuteCameraRow {
  camera_id: number;
  camera_name: string;
  scope: string;
  site_id: number;
  site_name: string;
  expires_at: number;
  muted_at: number;
  muted_by: string | null;
}

export interface SharedMutesResponse {
  sites?: SharedMuteSiteRow[];
  cameras?: SharedMuteCameraRow[];
}

const toMs = (seconds: number): number => Number(seconds) * 1000;

const durationMinutes = (mutedAt: number, expiresAt: number): number =>
  Math.max(0, Math.round((Number(expiresAt) - Number(mutedAt)) / 60));

/**
 * Maps the shared mute list into the shapes the Alerts page already works
 * with. camera rows with scope="site" (the remainder of a site-wide mute
 * after one of its cameras was unmuted) are plain camera mutes here.
 */
export const mapSharedMutes = (
  data: SharedMutesResponse | null | undefined
): { sites: MutedAlertSites; cameras: MutedAlertCameras } => {
  const sites: MutedAlertSites = (data?.sites ?? []).map((row) => ({
    site_id: row.site_id,
    site_name: row.site_name,
    ...(row.camera_id != null ? { camera_id: row.camera_id } : {}),
    mute_start_time: toMs(row.muted_at),
    mute_end_time: toMs(row.expires_at),
    mute_duration: durationMinutes(row.muted_at, row.expires_at),
    timezone: "",
    muted_by: row.muted_by ?? null,
  }));

  const cameras: MutedAlertCameras = (data?.cameras ?? []).map((row) => ({
    camera_id: row.camera_id,
    camera_name: row.camera_name,
    site_id: row.site_id,
    site_name: row.site_name,
    scope: row.scope,
    mute_start_time: toMs(row.muted_at),
    mute_end_time: toMs(row.expires_at),
    mute_duration: durationMinutes(row.muted_at, row.expires_at),
    timezone: "",
    muted_by: row.muted_by ?? null,
  }));

  return { sites, cameras };
};
